import React, { useState, useEffect } from 'react';
import { Save, Moon, Sun, Globe, Clock, DollarSign, Monitor, Palette, RotateCcw } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useTheme } from '../context/ThemeContext';
import toast from 'react-hot-toast';

const TIMEZONES = [
  'America/New_York','America/Chicago','America/Denver','America/Los_Angeles',
  'America/Anchorage','America/Honolulu','America/Phoenix',
  'America/Toronto','America/Vancouver','America/Halifax',
  'Europe/London','Europe/Paris','Europe/Berlin','Europe/Rome','Europe/Madrid',
  'Europe/Amsterdam','Europe/Brussels','Europe/Zurich','Europe/Stockholm',
  'Europe/Oslo','Europe/Copenhagen','Europe/Helsinki','Europe/Warsaw',
  'Europe/Prague','Europe/Vienna','Europe/Budapest','Europe/Bucharest',
  'Europe/Athens','Europe/Istanbul','Europe/Moscow',
  'Asia/Dubai','Asia/Karachi','Asia/Kolkata','Asia/Dhaka',
  'Asia/Bangkok','Asia/Singapore','Asia/Shanghai','Asia/Tokyo',
  'Asia/Seoul','Australia/Sydney','Australia/Melbourne','Australia/Perth',
  'Pacific/Auckland','Pacific/Fiji',
  'UTC',
];

const DATE_FORMATS = [
  { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY (US)' },
  { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY (EU)' },
  { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD (ISO)' },
  { value: 'MMM D, YYYY', label: 'Jan 1, 2025' },
  { value: 'D MMM YYYY',  label: '1 Jan 2025' },
];

const CURRENCIES = [
  { symbol: '$',  label: '$ — US Dollar' },
  { symbol: '€',  label: '€ — Euro' },
  { symbol: '£',  label: '£ — British Pound' },
  { symbol: 'CA$',label: 'CA$ — Canadian Dollar' },
  { symbol: 'AU$',label: 'AU$ — Australian Dollar' },
  { symbol: 'NZ$',label: 'NZ$ — New Zealand Dollar' },
  { symbol: 'CHF',label: 'CHF — Swiss Franc' },
  { symbol: '¥',  label: '¥ — Japanese Yen' },
  { symbol: '₹',  label: '₹ — Indian Rupee' },
  { symbol: 'R',  label: 'R — South African Rand' },
];

export default function PreferencesPage() {
  const { user } = useAuth();
  const { prefs, updatePref } = useTheme();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (prefs) setForm({ ...prefs });
  }, [prefs]);
  function set(key, value) {
    setForm(f => ({ ...f, [key]: value }));
  }

  async function save() {
    setSaving(true);
    try {
      for (const [key, value] of Object.entries(form)) {
        updatePref(key, value);
      }
      toast.success('Preferences saved');
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  }

  if (!form) return <div style={{ padding: 48, display: 'flex', justifyContent: 'center' }}><div className="spinner" /></div>;

  return (
    <div>
      <div className="page-header">
        <h2>Preferences</h2>
        <button className="btn btn-primary btn-sm" onClick={save} disabled={saving}>
          <Save size={14} /> {saving ? 'Saving…' : 'Save Preferences'}
        </button>
      </div>

      <div className="page-body">
        <div style={{ maxWidth: 640 }}>

          {/* Appearance */}
          <div className="card mb-4">
            <h3 style={{ fontSize: 13, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 6 }}>
              <Monitor size={14} /> Appearance
            </h3>

            <div className="settings-row">
              <div>
                <div className="settings-label">Dark Mode</div>
                <div className="settings-hint">Use a dark color scheme across the app</div>
              </div>
              <button
                onClick={() => set('dark_mode', !form.dark_mode)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '8px 14px', borderRadius: 8,
                  border: `1px solid ${form.dark_mode ? 'var(--accent)' : 'var(--border-strong)'}`,
                  background: form.dark_mode ? 'var(--accent)' : 'var(--bg-card)',
                  color: form.dark_mode ? 'white' : 'var(--text-secondary)',
                  cursor: 'pointer', fontSize: 13, fontWeight: 500,
                  transition: 'all 0.15s',
                }}
              >
                {form.dark_mode ? <Moon size={14} /> : <Sun size={14} />}
                {form.dark_mode ? 'Dark' : 'Light'}
              </button>
            </div>
          </div>

          {/* Locale */}
          <div className="card mb-4">
            <h3 style={{ fontSize: 13, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 6 }}>
              <Globe size={14} /> Locale & Regional
            </h3>

            <div className="settings-row">
              <div>
                <div className="settings-label">Timezone</div>
                <div className="settings-hint">Used for displaying all dates and times</div>
              </div>
              <select
                className="form-select"
                style={{ width: 220 }}
                value={form.timezone}
                onChange={e => set('timezone', e.target.value)}
              >
                {TIMEZONES.map(tz => (
                  <option key={tz} value={tz}>{tz.replace('_', ' ')}</option>
                ))}
              </select>
            </div>

            <div className="settings-row">
              <div>
                <div className="settings-label">Date Format</div>
                <div className="settings-hint">How dates are displayed throughout the app</div>
              </div>
              <select
                className="form-select"
                style={{ width: 220 }}
                value={form.date_format}
                onChange={e => set('date_format', e.target.value)}
              >
                {DATE_FORMATS.map(f => (
                  <option key={f.value} value={f.value}>{f.label}</option>
                ))}
              </select>
            </div>

            <div className="settings-row">
              <div>
                <div className="settings-label">Time Format</div>
                <div className="settings-hint">12-hour (AM/PM) or 24-hour clock</div>
              </div>
              <select
                className="form-select"
                style={{ width: 220 }}
                value={form.time_format}
                onChange={e => set('time_format', e.target.value)}
              >
                <option value="12hr">12-hour (1:30 PM)</option>
                <option value="24hr">24-hour (13:30)</option>
              </select>
            </div>
          </div>

          {/* Currency */}
          <div className="card mb-4">
            <h3 style={{ fontSize: 13, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 6 }}>
              <DollarSign size={14} /> Currency
            </h3>

            <div className="settings-row">
              <div>
                <div className="settings-label">Currency Symbol</div>
                <div className="settings-hint">Used for asset purchase prices and values</div>
              </div>
              <select
                className="form-select"
                style={{ width: 220 }}
                value={form.currency_symbol}
                onChange={e => set('currency_symbol', e.target.value)}
              >
                {CURRENCIES.map(c => (
                  <option key={c.symbol} value={c.symbol}>{c.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Colors */}
          <div className="card mb-4">
            <h3 style={{ fontSize: 13, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 6 }}>
              <Palette size={14} /> Color Overrides
            </h3>
            <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 16 }}>
              Customize colors for your account. Leave blank to use the default tenant theme.
            </p>

            <div className="settings-row">
              <div>
                <div className="settings-label">Primary Color</div>
                <div className="settings-hint">Buttons, links, and active states</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {form.theme_primary_color && (
                  <button
                    onClick={() => set('theme_primary_color', null)}
                    title="Reset to default"
                    style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: 2 }}
                  ><RotateCcw size={13} /></button>
                )}
                <div className="color-swatch" style={{ background: form.theme_primary_color || 'var(--accent)' }}>
                  <input type="color"
                    value={form.theme_primary_color || '#1a1a18'}
                    onChange={e => set('theme_primary_color', e.target.value)} />
                </div>
                <input
                  className="form-input"
                  style={{ width: 110, fontFamily: 'var(--font-mono)', fontSize: 12 }}
                  value={form.theme_primary_color || ''}
                  placeholder="Tenant default"
                  onChange={e => set('theme_primary_color', e.target.value || null)}
                />
              </div>
            </div>

            <div className="settings-row">
              <div>
                <div className="settings-label">Sidebar Color</div>
                <div className="settings-hint">Navigation sidebar background</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {form.theme_sidebar_color && (
                  <button
                    onClick={() => set('theme_sidebar_color', null)}
                    title="Reset to default"
                    style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: 2 }}
                  ><RotateCcw size={13} /></button>
                )}
                <div className="color-swatch" style={{ background: form.theme_sidebar_color || 'var(--sidebar-bg)' }}>
                  <input type="color"
                    value={form.theme_sidebar_color || '#1a1a18'}
                    onChange={e => set('theme_sidebar_color', e.target.value)} />
                </div>
                <input
                  className="form-input"
                  style={{ width: 110, fontFamily: 'var(--font-mono)', fontSize: 12 }}
                  value={form.theme_sidebar_color || ''}
                  placeholder="Tenant default"
                  onChange={e => set('theme_sidebar_color', e.target.value || null)}
                />
              </div>
            </div>
          </div>

          <button className="btn btn-primary" onClick={save} disabled={saving}>
            <Save size={14} /> {saving ? 'Saving…' : 'Save Preferences'}
          </button>

        </div>
      </div>
    </div>
  );
}
